"use client";

import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { AnimatePresence, motion } from "framer-motion";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import {
	CheckIcon,
	DevicePhoneMobileIcon,
	EnvelopeIcon,
	PencilSquareIcon,
} from "@heroicons/react/20/solid";
import { BulletSteps } from "@/components/ui/bulletStepsFMC";
import { PhoneValidation } from "@/components/forms/wif/parts/phoneValidationStep";
import { useMutation } from "@tanstack/react-query";
import {
	stateOption,
	FMC_FORM_TITLE,
	WEDO_INV_FIN_NAME,
	WEDO_INV_FIN_EMAIL,
	WEDO_INV_FIN_REF_ID,
} from "@/lib/constants";
import axios from "axios";
import { getCurrentTime, useSearchEngineDetector } from "@/lib/utils";
import Bowser from "bowser";
import ToggleSwitch from "@/components/ui/toggleSwitch";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RefObject } from "react";
import { RadioGroupField } from "@/components/ui/radioGroupField";

export type FormType = {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	state: string;
	creditHistory: string;
	creditorName: string;
	loanAmount: string;
	dateSubmitted: string;
};

async function postZap<T>(data: T) {
	return;
}

async function sendEmail<T>(data: T) {
	try {
		const siteUrl = window.location.origin;
		const response = await axios.post(`${siteUrl}/wp-json/contact-form/v1/send-email`, data);
		return response;
	} catch (error) {
		console.error("There was an error sending the email:", error);
	}
}

async function sendPartial<T>(data: T) {
	try {
		const siteUrl = window.location.origin;
		const response = await axios.post(`${siteUrl}/wp-json/partial-form/v1/send-partial`, data);
		return response;
	} catch (error) {
		console.error("There was an error sending the email:", error);
	}
}

const steps = [
	{
		id: "Step 1",
		name: "Personal Details",
		fields: ["firstName", "lastName", "email", "state"],
	},
	{
		id: "Step 2",
		name: "Verification",
		fields: ["phone"],
	},
	{
		id: "Success Page",
		name: "Completed",
		fields: [],
	},
];

function usePrevious<T>(value: T) {
	let ref = useRef(value);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

const variants = {
	initial: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
	animate: {
		opacity: 1,
	},
	exit: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
};

type QuickFormProps = {
	className?: string;
	loanTypes?: string;
	siteTitle?: string;
	rootId?: HTMLElement;
};

export function WeDoInvFin({ className, loanTypes, siteTitle, rootId }: QuickFormProps) {
	//Get values and setters from Zustand store
	const { formValues, setFormValue, resetFormValues, formErrors } = useFormDataStore();
	const {
		toggleTC,
		setToggleTC,
		selectedIndex,
		setSelectedIndex,
		isPhoneVerified,
		setPhoneVerified,
		isSubmitted,
		setIsSubmitted,
		setFormErrors,
		setValidateFormDetails,
		hasAttemptedSubmit,
		setHasAttemptedSubmit,
	} = useFormStateStore();

	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);
	const [tabHasFocus, setTabHasFocus] = useState(true);
	const savedFormState = loadFormState();
	const [browserInfo, setBrowserInfo] = useState({});
	const timer = useRef<NodeJS.Timeout | undefined>(undefined);
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState("");
	const prevIndex = usePrevious(selectedIndex);
	const isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex;
	const sourceUrl = window.location.href;
	const form = useForm<FormType>({
		defaultValues: savedFormState.values,
		mode: "onBlur",
	});

	const {
		getValues,
		trigger,
		register,
		setValue,
		clearErrors,
		formState: { errors },
	} = form;

	// Toggle Switch for Fixed Rate
	const [enabledTC, setEnabledTC] = useState(toggleTC);

	useEffect(() => {
		if (enabledTC) setHasAttemptedSubmit(false);
		setToggleTC(enabledTC);
	}, [enabledTC]);

	const firstName = form.watch("firstName");
	const lastName = form.watch("lastName");
	const email = form.watch("email");
	const state = form.watch("state");

	type FieldName = keyof FormType;

	const headingRef = useRef<HTMLHeadingElement>(null);
	const navDivRef = useRef<HTMLDivElement | null>(null); //References to the Navigation Button's Div Container

	// For Navigation Buttons (Previous, Next, Submit)
	useEffect(() => {
		const container = navDivRef.current;
		if (!container) return;

		const observer = new ResizeObserver(([entry]) => {
			let limiter = 0; // Determines if the nav div's width is too small for the nav buttons.
			selectedIndex === 0 ? (limiter = 310) : (limiter = 460);
			setIsNavDivSmall(entry.contentRect.width < limiter);
		});

		observer.observe(container);

		return () => observer.disconnect();
	}, [selectedIndex]);

	const scrollToHeading = useCallback(() => {
		if (window.matchMedia("(max-width: 767px)").matches) {
			setTimeout(() => {
				if (rootId) {
					const additionalOffset = 150;
					const targetPosition =
						rootId.getBoundingClientRect().top + window.scrollY - additionalOffset;

					window.scrollTo({
						top: targetPosition,
						behavior: "smooth",
					});
				}
			}, 100); // 100ms delay to ensure rendering
		}
	}, []);

	useEffect(() => {
		setSearchEngine(detectedSearchEngine);
	}, [detectedSearchEngine]);

	// Detect what browser
	useEffect(() => {
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
	}, []);

	window.leadGenerated = window.leadGenerated || false;

	const loadDataLayerScript = () => {
		if (!window.leadGenerated) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "generate_lead",
				enhanced_conversion_data: {
					email: email,
					phone_number: "+61" + formValues.phone?.substring(1),
					first_name: firstName,
					last_name: lastName,
				},
			});
			window.leadGenerated = true;
		}
	};

	const IP_STORAGE_KEY = "processedIPs";
	const EXPIRATION_TIME = 30 * 60 * 1000;

	const getProcessedIPs = (): { userIP: string; timestamp: number }[] => {
		const ips = localStorage.getItem(IP_STORAGE_KEY);
		return ips ? JSON.parse(ips) : [];
	};

	const addProcessedIP = (userIP: string) => {
		const ips = getProcessedIPs();
		const now = Date.now();
		const updatedIPs = ips.filter(({ timestamp }) => now - timestamp < EXPIRATION_TIME);
		updatedIPs.push({ userIP, timestamp: now });
		localStorage.setItem(IP_STORAGE_KEY, JSON.stringify(updatedIPs));
	};

	const isIPProcessed = (userIP: string): boolean => {
		const ips = getProcessedIPs();
		const now = Date.now();
		return ips.some(
			({ userIP: storedIP, timestamp }) =>
				storedIP === userIP && now - timestamp < EXPIRATION_TIME,
		);
	};

	// Detect what browser and user IP
	const getUserIP = useCallback(async () => {
		if (selectedIndex === 0) {
			const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/getIP`);
			const data = await response.data;
			setUserIP(data.ip);
			setUsingVPN(data.vpn);
		}
	}, [selectedIndex]);

	const sendPreMail = useMutation({
		mutationKey: [form.getValues()],
		mutationFn: async () => {
			const zapPost = {
				...form.getValues(),
				siteTitle: siteTitle,
				formName: `${siteTitle} ${WEDO_INV_FIN_NAME}`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: formValues.phone,
				state: state,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				dateSubmitted: getCurrentTime(),
				searchEngine: searchEngine,
				isReferrer: "Yes",
				partnerName: WEDO_INV_FIN_NAME || null,
				partnerEmail: WEDO_INV_FIN_EMAIL || null,
				referralId: WEDO_INV_FIN_REF_ID || null,
			};
			const data = await sendPartial(zapPost);
			return data;
		},
		onSuccess() {
			resetFormState();
			resetFormValues();
			setEnabledTC(false);
			setHasAttemptedSubmit(false);
		},
	});

	const sendMail = useMutation({
		mutationKey: [form.getValues()],
		mutationFn: async () => {
			const zapPost = {
				...form.getValues(),
				siteTitle: siteTitle,
				formName: `${siteTitle} ${WEDO_INV_FIN_NAME}`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: formValues.phone,
				state: state,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				dateSubmitted: getCurrentTime(),
				searchEngine: searchEngine,
				isReferrer: "Yes",
				partnerName: WEDO_INV_FIN_NAME || null,
				partnerEmail: WEDO_INV_FIN_EMAIL || null,
				referralId: WEDO_INV_FIN_REF_ID || null,
			};
			const data = await sendEmail(zapPost);
			return data;
		},
		onSuccess() {
			resetFormState();
			resetFormValues();
			setEnabledTC(false);
			setHasAttemptedSubmit(false);
		},
	});

	// Detect if tab is inactive
	useEffect(() => {
		const handleFocus = () => {
			setTabHasFocus(true);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};

		const handleBlur = () => {
			setTabHasFocus(false);
			timer.current = setTimeout(() => {
				// Handle the tab being inactive for 3 minutes here
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreMail.mutate();
					addProcessedIP(userIP);
				}
			}, 180000); // 180000 milliseconds = 3 minutes
		};

		window.addEventListener("focus", handleFocus);
		window.addEventListener("blur", handleBlur);

		// Clean up
		return () => {
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("blur", handleBlur);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};
	}, [isPhoneVerified, isSubmitted, userIP]);

	// Detect when tab is reloaded and closed
	useEffect(() => {
		if (selectedIndex !== 2) {
			const handleBeforeUnload = (event: any) => {
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreMail.mutate();
					addProcessedIP(userIP);
				}
				setPhoneVerified(false);
				setIsSubmitted(false);
				setSelectedIndex(0);
				resetFormValues();
			};

			window.addEventListener("beforeunload", handleBeforeUnload);
			window.addEventListener("pagehide", handleBeforeUnload);

			return () => {
				window.removeEventListener("beforeunload", handleBeforeUnload);
				window.removeEventListener("pagehide", handleBeforeUnload);
			};
		}
	}, [isPhoneVerified, isSubmitted, selectedIndex, userIP]);

	async function next() {
		setHasAttemptedSubmit(true);
		const fields = steps[selectedIndex].fields;

		const output = await trigger(fields as FieldName[], {
			shouldFocus: true,
		});

		if (!output) return;
		if (!enabledTC) return;

		if (selectedIndex < steps.length - 1) {
			scrollToHeading();
			setSelectedIndex(selectedIndex + 1);
		}
	}

	function prev() {
		if (selectedIndex > 0 && selectedIndex < steps.length - 1) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
		}
	}

	function close() {
		setSelectedIndex(0);
	}

	const handleStateChange = (value: string) => {
		form.setValue("state", value);
		clearErrors("state");
	};

	// Go to the next step after verification
	useEffect(() => {
		if (isPhoneVerified) {
			next();
		}
	}, [isPhoneVerified]);

	// Go to next page when phone is verified
	useEffect(() => {
		if (selectedIndex === 2 && isPhoneVerified && !isSubmitted) {
			sendMail.mutate();
			setIsSubmitted(true);
			loadDataLayerScript();
		}
		if (isSubmitted) {
			// Clear default values if the form has been submitted
			form.setValue("firstName", "");
			form.setValue("lastName", "");
			form.setValue("email", "");
			form.setValue("phone", "");
			form.setValue("state", "");
			resetFormState();
			resetFormValues();
			setPhoneVerified(false);
			setIsSubmitted(false);
			setEnabledTC(false);
		}
	}, [enabledTC, isPhoneVerified, selectedIndex, isSubmitted]);

	const hasFetched = useRef(false);

	useEffect(() => {
		if (hasFetched.current) return;
		if (selectedIndex !== 0) return;

		hasFetched.current = true;
		getUserIP();
	}, [selectedIndex, getUserIP]);

	// Save form state to local storage on changes
	useEffect(() => {
		const formValues = getValues();
		saveFormState({
			selectedIndex: selectedIndex,
			values: formValues,
			enabled: enabledTC,
			phoneVerified: isPhoneVerified,
		});
	}, [selectedIndex, enabledTC, getValues()]);

	// Resets local variables on refresh, remove the function below to maintain local storage
	useEffect(() => {
		resetFormState();
	}, []);

	const handleKeyDown = useCallback((event: any) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	}, []);

	return (
		<div className={clsx(className, "")}>
			{/* Form */}
			<AnimateChangeInHeight>
				<form
					className="jF-px-4 jF-pt-6 jF-pb-2 sm:jF-px-6"
					onKeyDown={handleKeyDown}
					onSubmit={(e) => e.preventDefault()}
				>
					<AnimatePresence initial={false} mode="wait" custom={isForwards}>
						{selectedIndex === 0 && (
							<motion.div
								key={0}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								variants={variants}
								custom={isForwards}
								className="jF-space-y-4"
							>
								<FormHeading headingRef={headingRef} />
								<div className="jF-space-y-2 jF-gap-x-2 sm:jF-flex sm:jF-gap-3 sm:jF-space-y-0">
									<div className="jF-flex-1">
										<div className="jF-relative jF-mt-2">
											<input
												{...register("firstName", {
													required: true,
												})}
												type="text"
												id="firstName"
												placeholder="First Name"
												className={clsx(
													errors.firstName
														? "jF-ring-red-500 focus:jF-ring-red-500"
														: "jF-ring-gray-300 focus:jF-ring-gray-900",
													"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-1 focus:jF-ring-inset jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
												)}
											/>
										</div>
										{errors.firstName && (
											<span className="jF-text-xs jF-text-red-500">
												First Name Required
											</span>
										)}
									</div>
									<div className="jF-flex-1">
										<div className="jF-relative jF-mt-2">
											<input
												{...register("lastName", {
													required: true,
												})}
												type="text"
												id="lastName"
												placeholder="Last Name"
												className={clsx(
													errors.lastName
														? "jF-ring-red-500 focus:jF-ring-red-500"
														: "jF-ring-gray-300 focus:jF-ring-gray-900",
													"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-1 focus:jF-ring-inset jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
												)}
											/>
										</div>
										{errors.lastName && (
											<span className="jF-text-xs jF-text-red-500">
												Last Name Required
											</span>
										)}
									</div>
								</div>
								<div className="jF-relative jF-mt-2">
									<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
										<EnvelopeIcon
											className="jF-h-5 jF-w-5 jF-text-gray-400"
											aria-hidden="true"
										/>
									</div>
									<input
										{...register("email", {
											required: true,
											pattern:
												/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
										})}
										type="email"
										id="email"
										autoComplete="email"
										placeholder="Email Address"
										className={clsx(
											errors.email
												? "jF-ring-red-500 focus:jF-ring-red-500"
												: "jF-ring-gray-300 focus:jF-ring-gray-900",
											"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-pl-10 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-1 focus:jF-ring-inset placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6 jF-bg-none",
										)}
									/>
								</div>
								{errors.email && (
									<span className="jF-text-xs jF-text-red-500">
										Email is not correctly formatted
									</span>
								)}
								<div className="jF-relative jF-mt-2">
									<RadioGroupField
										{...register("state", {
											required: true,
											validate: (value) => {
												if (value === "") {
													return false;
												} else {
													return true;
												}
											},
										})}
										value={state ? state : ""}
										id="state"
										label=""
										onChange={(value) => handleStateChange(value)}
										required
										error={errors.state ? "State is required" : ""}
										gridCols={isNavDivSmall ? 2 : 8}
										showIcons={false}
										className="jF-mt-4 jF-justify-center jF-w-full"
										options={stateOption
											.filter((s) => s.value)
											.map((s) => ({
												id: s.value,
												value: s.value,
												label: s.option,
											}))}
									/>
								</div>
								<div className="!jF-mt-4">
									<ToggleSwitch
										label=""
										enabled={enabledTC}
										setEnabled={setEnabledTC}
									/>
									{hasAttemptedSubmit && !enabledTC && (
										<span className="jF-block jF-mt-1 jF-text-xs jF-text-red-500">
											Please agree to the terms and conditions to continue
										</span>
									)}
								</div>
							</motion.div>
						)}
						{selectedIndex === 1 && (
							<motion.div
								key={1}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								{!isPhoneVerified ? (
									<>
										<OTPHeading headingRef={headingRef} />
										<PhoneValidation />
									</>
								) : (
									<>
										<FormVerified headingRef={headingRef} />
									</>
								)}
							</motion.div>
						)}
						{selectedIndex === 2 && (
							<motion.div
								key={2}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="space-y-4"
							>
								<FormSuccessHeading headingRef={headingRef} />
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</AnimateChangeInHeight>

			{/* Navigation */}
			{selectedIndex !== 2 ? (
				<div
					ref={navDivRef}
					className="jF-flex jF-items-center jF-flex-wrap jF-justify-center jF-border-t jF-border-gray-200 jF-bg-white jF-mt-2 jF-py-3 jF-px-4"
				>
					<BulletSteps
						steps={steps}
						selectedIndex={selectedIndex}
						navRef={isNavDivSmall}
					/>
					<div
						className={clsx(
							selectedIndex === 2 ? "jF-hidden" : "jF-flex jF-flex-1 jF-justify-end",
						)}
					>
						<button
							disabled={selectedIndex === 1 && !isPhoneVerified}
							className={clsx(
								selectedIndex === 0 || (selectedIndex === 1 && !isPhoneVerified)
									? "jF-hidden"
									: isNavDivSmall
										? "jF-w-full"
										: "jF-w-auto",
								"jF-bg-white hover:jF-bg-gray-50 jF-relative jF-inline-flex jF-items-center jF-rounded-md jF-justify-center jF-px-3 jF-py-2 jF-text-md jF-font-semibold jF-text-gray-900 jF-ring-1  jF-ring-gray-300 focus-visible:jF-outline-offset-0",
							)}
							onClick={() => prev()}
						>
							Previous
						</button>
						<button
							className={clsx(
								(selectedIndex === 0 && !toggleTC) ||
									(selectedIndex === 1 && !isPhoneVerified)
									? "jF-bg-gray-100 hover:jF-cursor-not-allowed jF-text-gray-400 jF-ring-gray-100"
									: isNavDivSmall
										? "jF-bg-[--jF-theme-color] hover:jF-bg-[--jF-hover-color] jF-w-full"
										: "jF-bg-[--jF-theme-color] hover:jF-bg-[--jF-hover-color] jF-w-auto",
								"jF-relative jF-ml-3 jF-inline-flex jF-items-center jF-rounded-md jF-justify-center jF-px-3 jF-py-2 jF-text-md jF-font-semibold jF-text-white jF-ring-1 jF-ring-[--jF-theme-color] focus-visible:jF-outline-offset-0",
							)}
							onClick={() => {
								if (selectedIndex !== 1 || isPhoneVerified) {
									next();
								}
							}}
						>
							{selectedIndex === 1 ? "Submit" : "Next"}
						</button>
					</div>
				</div>
			) : (
				<div className="jF-flex jF-items-center jF-flex-wrap jF-justify-center jF-border-t jF-border-gray-200 jF-bg-white jF-mt-2 jF-px-4 jF-py-3 sm:jF-px-6 sm:jF-justify-between">
					<button
						className="jF-bg-[--jF-theme-color] hover:jF-bg-[--jF-hover-color] jF-relative jF-mb-2 jF-mx-auto jF-block jF-w-1/3 jF-text-center jF-rounded-md jF-px-3 jF-py-2 text-md jF-font-semibold jF-text-white jF-ring-1 jF-ring-[--jF-theme-color] focus-visible:jF-outline-offset-0"
						onClick={() => {
							close();
						}}
					>
						Close
					</button>
				</div>
			)}
		</div>
	);
}

const FORM_STATE_KEY = "formState";

function saveFormState(state: {
	selectedIndex: number;
	values: FormType;
	phoneVerified: boolean;
	enabled: boolean;
}) {
	sessionStorage.setItem(FORM_STATE_KEY, JSON.stringify(state));
}

function loadFormState(): {
	selectedIndex: number;
	enabled: boolean;
	values: FormType;
	phoneVerified: boolean;
} {
	const state = sessionStorage.getItem(FORM_STATE_KEY);
	return state
		? JSON.parse(state)
		: {
				selectedIndex: 0,
				values: {} as FormType,
				phoneVerified: false,
				enabled: false,
			};
}

function resetFormState() {
	sessionStorage.clear();
	sessionStorage.removeItem(FORM_STATE_KEY);
}

function OTPHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef?: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "")}>
				<div className="jF-mx-auto jF-flex jF-h-20 jF-w-20 jF-items-center jF-justify-center jF-rounded-full jF-bg-green-100">
					<DevicePhoneMobileIcon
						className="jF-h-10 jF-w-10 jF-text-[--jF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="jF-my-3 jF-text-center sm:jF-mt-5">
					<h2
						ref={headingRef}
						className="!jF-text-2xl jF-font-semibold jF-leading-6 jF-text-gray-900"
					>
						Verify Your Phone
					</h2>
				</div>
			</div>
		</>
	);
}

function FormHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef?: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "")}>
				<div className="jF-mx-auto jF-flex jF-h-20 jF-w-20 jF-items-center jF-justify-center jF-rounded-full jF-bg-green-100">
					<PencilSquareIcon
						className="jF-h-10 jF-w-10 jF-text-[--jF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="jF-my-3 jF-text-center sm:jF-mt-5">
					<h2
						ref={headingRef}
						className="!jF-text-2xl jF-mb-3 jF-font-semibold jF-text-[--jF-theme-h-text] jF-leading-8 sm:jF-leading-8"
					>
						{FMC_FORM_TITLE}
					</h2>
				</div>
			</div>
		</>
	);
}

function FormSuccessHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef?: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "")}>
				<div className="jF-mx-auto jF-flex jF-h-20 jF-w-20 jF-items-center jF-justify-center jF-rounded-full jF-bg-green-100">
					<CheckIcon
						className="jF-h-10 jF-w-10 jF-text-[--jF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="jF-my-3 jF-text-center sm:jF-mt-5">
					<h2
						ref={headingRef}
						className="!jF-text-2xl jF-mb-3 jF-font-semibold jF-text-[--jF-theme-h-text] jF-leading-6"
					>
						Enquiry Received
					</h2>
					<div className="jF-mt-5 jF-mb-5">
						<p className="jF-text-md jF-text-gray-500">
							Thanks for contacting us! We will get in touch with you shortly.
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

function FormVerified({
	className,
	headingRef,
}: {
	className?: string;
	headingRef?: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "")}>
				<div className="jF-mx-auto jF-flex jF-h-20 jF-w-20 jF-items-center jF-justify-center jF-rounded-full jF-bg-green-100">
					<CheckIcon
						className="jF-h-10 jF-w-10 jF-text-[--jF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="jF-my-3 jF-text-center sm:jF-mt-5">
					<h2
						ref={headingRef}
						className="!jF-text-2xl jF-text-[--jF-theme-h-text] jF-mb-3 jF-font-semibold jF-leading-6"
					>
						Thank you!
					</h2>
					<div className="jF-mt-5 jF-mb-5">
						<p className="jF-text-md jF-text-gray-500">Successfully verified</p>
					</div>
				</div>
			</div>
		</>
	);
}
